import React, { useContext, useRef, useState, useEffect } from 'react';
import { Translate } from '@/components';
import { cx, css } from '@emotion/css';
import { Flyer } from '@/graphql/types';
import getSelectedLanguage from '@/utils/getSelectedLanguage';
import DateFnsUtils from '@date-io/date-fns';
import * as dateFnsLocale from 'date-fns/locale';
import utcDateToMidnightLocal from '@/utils/utcDateToMidnightLocal';
import { groupBy, sortBy } from 'lodash';
import UserPreferencesContext from '@/context/UserPreferencesContext';
import styles from './Calendar.styles';
import { DayCard } from '../DayCard/DayCard';

interface CalendarProps {
  trackId?: string;
  stickyTop: number;
  flyers: Array<Flyer>;
}
export const Calendar: React.FC<CalendarProps> = ({ trackId, stickyTop, flyers }) => {
  const { language } = useContext(UserPreferencesContext);
  const selectedLanguage = getSelectedLanguage(language.code);
  const dateFns = new DateFnsUtils({ locale: dateFnsLocale[selectedLanguage] });
  const [monthsNameVisible, setMonthsNameVisible] = useState<Array<boolean>>([]);
  const monthParallaxRef = useRef<Array<HTMLDivElement | null>>([]);
  const groupedFlyersPerWeek = groupBy(
    sortBy(flyers, flyer => flyer.startDate),
    flyer => dateFns.startOfWeek(utcDateToMidnightLocal(new Date(flyer.startDate)))
  );

  const groupdFlyersPerMonth = groupBy(Object.keys(groupedFlyersPerWeek), date =>
    dateFns.startOfMonth(utcDateToMidnightLocal(new Date(date)))
  );

  const getWeekName = (startWeek: string) => {
    const startOfWeek = dateFns.format(new Date(startWeek), 'monthAndDate');
    const endOfWeekDate = dateFns.endOfWeek(utcDateToMidnightLocal(new Date(startWeek)));
    let endOfWeek = '';
    if (startOfWeek.includes(dateFns.format(new Date(endOfWeekDate), 'month'))) {
      endOfWeek = dateFns.formatByString(new Date(endOfWeekDate), 'd');
    } else {
      endOfWeek = dateFns.format(new Date(endOfWeekDate), 'monthAndDate');
    }

    return `${startOfWeek} - ${endOfWeek}`;
  };

  const scrollHandler = () => {
    if (monthParallaxRef?.current) {
      const monthsNames: Array<boolean> = [];
      monthParallaxRef.current?.forEach(month => {
        if (month) {
          monthsNames.push(window.pageYOffset > month.offsetTop - 50);
        }
      });
      setMonthsNameVisible(monthsNames);
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', scrollHandler);

    return () => window.removeEventListener('scroll', scrollHandler);
  }, []);

  return (
    <>
      {Object.keys(groupdFlyersPerMonth).map((monthKey, monthIndex) => {
        const monthName = dateFns.format(new Date(monthKey), 'month').toLowerCase();
        const monthNumber = Number(dateFns.formatByString(new Date(monthKey), 'M'));
        return (
          <div key={monthKey}>
            <div
              className={styles.monthContainer}
              ref={el => {
                monthParallaxRef.current[monthIndex] = el;
              }}
            >
              <div
                className={cx(
                  styles.parallax,
                  css`
                    background-image: none;
                  `
                )}
              />
              <div className={styles.monthName} data-testid="monthName">
                <Translate>{monthName}</Translate>
              </div>
            </div>
            <div className={styles.month} style={{ top: `${stickyTop}px` }}>
              {monthsNameVisible[monthIndex] && <Translate>{monthName}</Translate>}
            </div>
            {groupdFlyersPerMonth[monthKey].map(weekKey => {
              const flyersPerDay = groupBy(groupedFlyersPerWeek[weekKey], flyer =>
                dateFns.startOfDay(utcDateToMidnightLocal(new Date(flyer.startDate)))
              );
              return (
                <div className={styles.calendar} key={weekKey}>
                  <div
                    className={styles.weekName}
                    style={{ top: `${stickyTop}px` }}
                    data-testid="stickyWeekContainer"
                  >
                    <Translate>{getWeekName(weekKey)}</Translate>
                  </div>
                  {Object.keys(flyersPerDay).map(dayKey => {
                    const weekDay = dateFns
                      .format(new Date(dayKey), 'weekdayShort')
                      .substring(0, 3);
                    const day = dateFns.formatByString(new Date(dayKey), 'dd');
                    return (
                      <DayCard
                        key={dayKey}
                        trackId={trackId}
                        stickyTop={stickyTop}
                        day={day}
                        weekDay={weekDay}
                        flyers={flyersPerDay[dayKey]}
                      />
                    );
                  })}
                </div>
              );
            })}
          </div>
        );
      })}
    </>
  );
};
